import React from "react";
import { type GetStaticProps, type NextPage } from "next";
import { HalfHero } from "components/half-hero";
import { Section } from "components/section";
import { Page } from "components/page";
import { CenteredQuote } from "components/quotes";
import { Customers } from "components/logos";
import { DemoEmbedHalf } from "components/demo-embed";
import DestinationMarquee from "components/destination-marquee";
import {
  type IntegrationSimple,
  type Page as SanityPage,
  type Banner,
  getAnnouncementBanner,
  getPage,
  getSimpleIntegrations,
} from "lib/sanity";
import { getPageMetaImage } from "utils";
import { PairImage } from "components/pair-image";
import { AuraPlate } from "ui/plates";
import heroImage from "../../../public/assets/solutions/ecommerce/hero.png";
import firstPairImage from "../../../public/assets/solutions/ecommerce/pair-1.png";
import secondPairImage from "../../../public/assets/solutions/ecommerce/pair-2.png";

type PageProps = {
  page: SanityPage;
  destinations: IntegrationSimple[];
  announcementBanner?: Banner;
};

const Ecommerce: NextPage<Readonly<PageProps>> = ({ page, destinations, announcementBanner }) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <HalfHero
        hideSecondary
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        image={heroImage}
        imageRatio={3 / 2}
        imageObjectFit="contain"
      />

      {page.customers && <Customers title="Trusted by marketing teams at" customers={page.customers} my={24} />}

      <DemoEmbedHalf
        titleSize="md"
        title="Give every customer a personalized shopping experience"
        description="Leverage the customer data you already have to build personalized shopping journeys."
      />

      <Section>
        <PairImage
          align="right"
          image={firstPairImage}
          title="Hyper-personalize customer journeys"
          description="Activate all of your valuable customer data to ensure your teams are able to hyper-personalize every customer interaction. Product recommendation campaigns made easy."
        />
      </Section>

      <Section>
        <PairImage
          align="left"
          image={secondPairImage}
          title="Centralize audience management"
          description="Centrally build and sync audiences to any marketing tool or ad platform with a no-code audience builder. Target your most valuable customers across channels. No CSV uploads required."
        />
      </Section>

      <AuraPlate condensed variant="blue" py={0}>
        <CenteredQuote
          quoteText="We're syncing millions of records every day to personalize journeys for 65+ million members in our loyalty program"
          author={{ name: "Bradley Breuer", job: "VP of Marketing" }}
          company={{
            name: "PetSmart",
            logo: "https://cdn.sanity.io/images/pwmfmi47/production/375df10710b9b2fbd2cf9c2ae5f669672753022d-607x150.png",
          }}
          cta={{
            href: "/customers/petsmart",
            text: "Read the story",
          }}
        />
      </AuraPlate>

      <DestinationMarquee destinations={destinations} />
    </Page>
  );
};

export default Ecommerce;

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, destinations, announcementBanner] = await Promise.all([
    getPage("/solutions/ecommerce"),
    getSimpleIntegrations("destination"),
    getAnnouncementBanner(),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      destinations,
      announcementBanner,
    },
    revalidate: 60,
  };
};
